import { onBeforeUnmount, onMounted } from "vue";

/**
 * Blocks the browser's own pinch / double-tap magnifier on touch devices, so the ONLY way to resize
 * the UI is the in-app zoom (`usePageZoom` → `--alkokh-page-zoom`), which re-lays the app out at a
 * larger element size instead of scaling a bitmap of the viewport.
 *
 * ⚠️ CSS AND THE VIEWPORT META ARE BOTH INSUFFICIENT ALONE. `touch-action: pan-x pan-y`
 * (`styles.scss`) covers Chrome/Android, and `user-scalable=no` (`index.html`) covers older
 * WebViews — but iOS Safari ignores BOTH for pinch. Its proprietary `gesture*` events are the only
 * hook there, hence this guard.
 *
 * ⚠️ NEVER BINDS `touchstart`, and never calls `preventDefault` on a single-finger move. Both
 * `usePullToRefresh` and `useSheetDrag` document the flatpickr bug in docs/AGENT.md: a teleported
 * calendar's outside-click test runs on `touchstart`, BEFORE the tap's `click`, so a global handler
 * on that event swallows taps app-wide. Multi-touch moves are safe because nothing in the app reads
 * them — `usePullToRefresh` explicitly bails on `touches.length !== 1`.
 *
 * ⚠️ Escape hatch: anything that implements its OWN pinch (image/DICOM viewers) opts out by carrying
 * `data-allow-pinch`, or by matching one of `PINCH_ALLOWED_SELECTORS`.
 *
 * ⚠️ Coarse-pointer only. Desktop mouse and trackpad behaviour — including the browser's own
 * ctrl+wheel zoom — is deliberately left alone.
 */

/** Subtrees that own a pinch gesture of their own and must keep receiving it. */
const PINCH_ALLOWED_SELECTORS = "[data-allow-pinch], .dicom-viewer__stage";

function isPinchAllowed(target: EventTarget | null) {
  return target instanceof Element && Boolean(target.closest(PINCH_ALLOWED_SELECTORS));
}

export function usePinchZoomGuard() {
  function onGesture(event: Event) {
    if (isPinchAllowed(event.target))
      return;

    if (event.cancelable)
      event.preventDefault();
  }

  function onTouchMove(event: TouchEvent) {
    // Single-finger moves are scrolling, pull-to-refresh and sheet drags. Never touch them.
    if (event.touches.length < 2)
      return;

    if (isPinchAllowed(event.target))
      return;

    if (event.cancelable)
      event.preventDefault();
  }

  function bind() {
    // iOS Safari only; a no-op everywhere else.
    document.addEventListener("gesturestart", onGesture, { passive: false });
    document.addEventListener("gesturechange", onGesture, { passive: false });
    document.addEventListener("gestureend", onGesture, { passive: false });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
  }

  function unbind() {
    document.removeEventListener("gesturestart", onGesture);
    document.removeEventListener("gesturechange", onGesture);
    document.removeEventListener("gestureend", onGesture);
    document.removeEventListener("touchmove", onTouchMove);
  }

  onMounted(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    const isCoarsePointer = window.matchMedia?.("(pointer: coarse)").matches
      || "ontouchstart" in window;

    if (!isCoarsePointer)
      return;

    bind();
  });

  onBeforeUnmount(() => {
    if (typeof document !== "undefined")
      unbind();
  });
}
